import { Controller } from "@hotwired/stimulus";

// City/airport autocomplete for the search field, replacing jQuery-UI
// .autocomplete(). Talks to the same /autocomplete/:type/:search_type
// endpoint (FapiController#autocomplete -> FAPI::Autocomplete.search),
// response shape: { term, cities: [{ id, fullname, name, city, country,
// iata, airports: [{ id, fullname, ... }] }], airports: [{ id, fullname,
// ... }] } -- standalone airport matches not grouped under a city.
export default class extends Controller {
  static targets = ["input", "hiddenId", "list"];
  static values = { url: String, minLength: { type: Number, default: 2 } };

  connect() {
    this.debounceTimer = null;
    this.activeIndex = -1;
    this.boundClose = this.closeOnOutsideClick.bind(this);
    document.addEventListener("click", this.boundClose);
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose);
    clearTimeout(this.debounceTimer);
  }

  // Focusing a field that already holds a city selects its text, so typing
  // replaces the destination instead of appending to it. Without this the
  // visitor had to select the old value and delete it by hand before they
  // could change where they were flying -- fiddly with a mouse, worse with a
  // thumb. The old design did the same (searchfield_functions.ts bound
  // .airport_field's click to target.select()).
  //
  // Only on focus, so a second click inside an already-focused field still
  // places the caret for an edit rather than re-selecting everything.
  focus() {
    if (this.inputTarget.value.trim() !== "") this.inputTarget.select();
    this.suggest({ immediate: true });
  }

  search() {
    this.suggest();
  }

  suggest({ immediate = false } = {}) {
    const term = this.inputTarget.value.trim();
    clearTimeout(this.debounceTimer);

    // An empty field is not "nothing to search": the endpoint answers term=""
    // with the gate's own popular destinations (Stockholm, Köpenhamn, Malmö,
    // Göteborg on flygcity), which is what the old design showed when you
    // clicked an empty field -- it ran with minLength 0 and fired a search on
    // focus. So length 0 asks, and asks straight away, since there is no
    // typing to debounce.
    //
    // A single character still does not: it matches most of the world and
    // costs a request per keystroke. Only the 1-character case is skipped;
    // that is the one difference from the old behaviour, which queried on
    // every keystroke from the first.
    if (term.length > 0 && term.length < this.minLengthValue) {
      this.close();
      return;
    }

    this.debounceTimer = setTimeout(() => this.fetchSuggestions(term), immediate || term.length === 0 ? 0 : 200);
  }

  async fetchSuggestions(term) {
    const requestTerm = term;
    let response;
    try {
      response = await fetch(`${this.urlValue}?term=${encodeURIComponent(term)}`, {
        headers: { Accept: "application/json" },
      });
    } catch (e) {
      return;
    }
    if (!response.ok) return;
    // Guard against a slower earlier request resolving after a newer one.
    if (this.inputTarget.value.trim() !== requestTerm) return;

    const data = await response.json();
    this.render(data);
  }

  // City icon: a filled map-pin, the natural "place" glyph. Airport icon: a
  // plane-in-circle badge, visually distinct at a glance and consistent
  // with how Google Flights/Skyscanner separate "city, any airport" from
  // a specific named airport. Airport sub-rows nest under their parent
  // city with a connecting rule instead of just extra padding, so the
  // hierarchy reads immediately instead of just looking like smaller text.
  cityIcon() {
    return `<svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M9.69 18.933a.75.75 0 00.62 0c.116-.052.281-.132.485-.242a15.8 15.8 0 002.273-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a15.8 15.8 0 002.273 1.765c.204.11.37.19.485.242zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" clip-rule="evenodd" /></svg>`;
  }

  airportIcon() {
    return `<svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M10.75 2.5a.75.75 0 00-1.5 0v3.44L3.29 9.51a.75.75 0 00-.39.66v1.28a.5.5 0 00.65.48l5.65-1.9v3.4l-1.68 1.17a.5.5 0 00-.22.42v1.02a.4.4 0 00.53.38l2.17-.72 2.17.72a.4.4 0 00.53-.38v-1.02a.5.5 0 00-.22-.42L10.75 12.4V9l5.65 1.9a.5.5 0 00.65-.48V9.15a.75.75 0 00-.39-.66l-5.96-3.55V2.5z" /></svg>`;
  }

  // fullname arrives as "City name (IATA), Country" -- split it back apart
  // to give the dropdown a proper two-line row (bold place name + IATA
  // badge, gray country/region underneath) instead of one long plain
  // string, which is what made the previous version read as a bare list
  // of button labels rather than a real location picker.
  splitLabel(fullname) {
    const match = fullname.match(/^(.*?)\s*\(([A-Z]{3})\)(?:,\s*(.*))?$/);
    if (!match) return { name: fullname, code: null, region: null };
    return { name: match[1], code: match[2], region: match[3] || null };
  }

  // Grid, not flex, for the icon+text row: two flex children sharing space
  // (icon + a `flex-1 min-w-0` text column) turned out to still refuse to
  // shrink the name below a sliver in testing, even with min-w-0 present at
  // every level -- with a `<button>` as the flex container, an `<svg>`
  // inside the icon badge, and a nested nowrap+baseline row for name/code,
  // there were enough nested flex contexts that one of them was sizing to
  // its content instead of the available track. A CSS grid with a fixed
  // `auto` icon column and a `1fr` text column doesn't have that failure
  // mode: the second track is explicitly the remaining space, full stop,
  // so `truncate` on a grid-item text node reliably has something correct
  // to measure against.
  renderRow(item) {
    const { name, code, region } = this.splitLabel(item.label);
    const icon = item.indent ? this.airportIcon() : this.cityIcon();
    // `block w-full float-none` on every <li> counters a legacy bare `li`
    // rule (themes/general/desktop/styles/layout/_layout.scss) that floats
    // list items left -- without an explicit override, a floated element
    // with no declared width shrinks-to-fit its content, which collapsed
    // this row down to roughly icon-width instead of filling the dropdown,
    // squeezing the name/code text into a sliver regardless of how the
    // row's own internal flex/grid tracks were set up. The original
    // pre-rewrite version had this exact override (`float-none`); it was
    // dropped when this row template was rebuilt and needs restoring.
    return `
      <li class="block w-full float-none ${item.indent ? "relative ml-5 border-l border-gray-100 pl-3" : ""}">
        <button type="button"
          class="grid w-full grid-cols-[2rem_1fr] items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-50"
          data-action="click->autocomplete#select"
          data-id="${item.id}"
          data-label="${item.label.replace(/"/g, "&quot;")}">
          <span class="flex h-8 w-8 items-center justify-center rounded-full ${item.indent ? "bg-gray-100 text-gray-400" : "bg-primary-light text-primary"}">${icon}</span>
          <span class="min-w-0">
            <span class="grid grid-cols-[1fr_auto] items-center gap-1.5">
              <span class="truncate text-sm font-medium text-gray-900">${name}</span>
              ${code ? `<span class="text-xs font-medium text-gray-400">${code}</span>` : ""}
            </span>
            ${region ? `<span class="block truncate text-xs text-gray-500">${region}</span>` : ""}
          </span>
        </button>
      </li>`;
  }

  render(data) {
    const items = [];
    (data.cities || []).forEach((city) => {
      items.push({ id: city.id, label: city.fullname, sublabel: null });
      (city.airports || []).forEach((airport) => {
        items.push({ id: airport.id, label: airport.fullname, sublabel: null, indent: true });
      });
    });
    (data.airports || []).forEach((airport) => {
      items.push({ id: airport.id, label: airport.fullname, sublabel: null });
    });

    if (items.length === 0) {
      this.close();
      return;
    }

    this.listTarget.innerHTML = items.map((item) => this.renderRow(item)).join("");
    this.activeIndex = -1;
    this.open();
  }

  select(event) {
    const { id, label } = event.currentTarget.dataset;
    this.inputTarget.value = label;
    this.hiddenIdTarget.value = id;
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    // A city was actually picked from the list, which is not the same thing
    // as the `change` above: a text input fires that on blur after any edit,
    // including one that never resolved to a city. The home page's collapsed
    // search field keys off this (search_expand_controller.js) and must not
    // open for someone who typed three letters and clicked away.
    this.dispatch("selected", { detail: { id, label } });
    this.close();
  }

  // ArrowDown/ArrowUp move a highlighted row (wrapping at either end),
  // Enter selects it exactly like a click would, Escape closes without
  // selecting. Previously nothing handled keydown at all here -- ArrowDown
  // did nothing, so the only way to pick a suggestion was a mouse click.
  keydown(event) {
    if (this.listTarget.classList.contains("hidden")) return;
    const rows = Array.from(this.listTarget.querySelectorAll("[data-action*='autocomplete#select']"));
    if (rows.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.activeIndex = (this.activeIndex + 1) % rows.length;
      this.highlight(rows);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.activeIndex = (this.activeIndex - 1 + rows.length) % rows.length;
      this.highlight(rows);
    } else if (event.key === "Enter") {
      if (this.activeIndex < 0) return;
      event.preventDefault();
      rows[this.activeIndex].click();
    } else if (event.key === "Escape") {
      this.close();
    }
  }

  highlight(rows) {
    rows.forEach((row, index) => row.classList.toggle("bg-gray-50", index === this.activeIndex));
    rows[this.activeIndex].scrollIntoView({ block: "nearest" });
  }

  open() {
    this.listTarget.classList.remove("hidden");
  }

  close() {
    this.listTarget.classList.add("hidden");
    this.listTarget.innerHTML = "";
    this.activeIndex = -1;
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close();
  }
}
